import React, { useState, useEffect } from "react";

interface FileGeneratingLoaderProps {
  isGenerating: boolean;
}

const FileGeneratingLoader: React.FC<FileGeneratingLoaderProps> = ({ isGenerating }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = [
    { title: "Connexion aux services IA..." },
    { title: "Analyse de la structure des tâches & dépendances..." },
    { title: "Génération du planning Gantt Excel..." },
    { title: "Finalisation et préparation du téléchargement..." },
  ];

  useEffect(() => {
    if (isGenerating) {
      setCurrentStepIndex(0);
      const timer1 = setTimeout(() => setCurrentStepIndex(1), 1500);
      const timer2 = setTimeout(() => setCurrentStepIndex(2), 3500);
      const timer3 = setTimeout(() => setCurrentStepIndex(3), 6000);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [isGenerating]);

  if (!isGenerating) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="w-full max-w-xl bg-[#131622] border border-white/10 rounded-2xl p-6 shadow-2xl text-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 mb-5 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <h3 className="font-display font-semibold text-base text-white">
              Génération du planning Gantt
            </h3>
          </div>
          <span className="font-mono text-xs font-semibold text-purple-400">
            {currentStepIndex < 3 ? "Traitement en cours..." : "Téléchargement imminent"}
          </span>
        </div>

        {/* Content Box */}
        <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
          {/* Glowing Animated Icon */}
          <div className="relative flex items-center justify-center w-20 h-20 shrink-0">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-purple-600/30 to-cyan-500/30 blur-lg animate-pulse" />
            <div className="relative w-16 h-16 rounded-2xl bg-[#1a1e2e] border border-purple-500/40 flex items-center justify-center shadow-inner">
              <svg className="w-8 h-8 text-cyan-400 animate-pulseSlow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
              </svg>
            </div>
          </div>

          {/* Checklist */}
          <div className="flex-1 w-full space-y-2 font-mono text-xs">
            {steps.map((step, idx) => {
              const isDone = idx < currentStepIndex;
              const isCurrent = idx === currentStepIndex;

              return (
                <div
                  key={idx}
                  className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                    isCurrent
                      ? "bg-white/5 border border-purple-500/20 text-purple-200"
                      : isDone
                      ? "text-slate-300"
                      : "text-slate-600"
                  }`}
                >
                  <div className="flex items-center gap-2 truncate mr-2">
                    {isDone ? (
                      <span className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center text-[10px] font-bold shrink-0">
                        ✓
                      </span>
                    ) : isCurrent ? (
                      <span className="w-4 h-4 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center justify-center text-[10px] animate-spin shrink-0">
                        ⟳
                      </span>
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-white/5 text-slate-600 flex items-center justify-center text-[10px] shrink-0">
                        ⏳
                      </span>
                    )}
                    <span className="truncate">{step.title}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0">
                    {isDone ? "Terminé" : isCurrent ? "En cours" : "En attente"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Output format badge */}
        <div className="p-2.5 rounded-xl bg-[#181c2b] border border-white/5 flex items-center gap-2 text-xs text-slate-400">
          <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>
            Format de sortie :{" "}
            <strong className="text-cyan-400 font-medium">Microsoft Excel (.xlsx)</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

export default FileGeneratingLoader;
